import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '@contexts/AuthContext';
import { useHousehold } from '@contexts/HouseholdContext';
import { streamFlowApi, setActiveHouseholdId } from '@lib/streamFlowApi';

/**
 * Recipient-side invite landing page.
 *
 * Flow:
 *   1. Unauthenticated visitor → redirect to /login?next=/invite/<token>
 *      (login page reads `next` and routes back here after sign-in)
 *   2. Authenticated visitor → fetch invite preview, show "Join
 *      'Smith Household' as member" + Accept
 *   3. Accept → POST /api/invites/:token/accept; on success, switch
 *      the active household to the new one and redirect to /
 *
 * The invite preview shows the original invited_email so a user
 * signing up with a slightly different address can see "this was
 * sent to alice@example.com — you signed in as alice+plus@example.com"
 * before accepting.
 */
export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const { session, loading: authLoading } = useAuth();
  const { refresh, setActiveId } = useHousehold();
  const navigate = useNavigate();

  type InvitePreview = NonNullable<Awaited<ReturnType<typeof streamFlowApi.getInviteByToken>>['data']>;
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!session) {
      // Bounce to /login, preserving the redirect path so login can
      // bring us back here.
      navigate(`/login?next=${encodeURIComponent(`/invite/${token}`)}`, { replace: true });
      return;
    }
    if (!token) {
      setError('Missing invite token');
      setLoading(false);
      return;
    }
    let cancelled = false;
    streamFlowApi.getInviteByToken(token).then((res) => {
      if (cancelled) return;
      if (!res.success || !res.data) {
        setError(res.error?.message ?? 'Invite not found');
      } else {
        setPreview(res.data);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [authLoading, session, token, navigate]);

  const onAccept = async () => {
    if (!token) return;
    setAccepting(true);
    const res = await streamFlowApi.acceptInvite(token);
    setAccepting(false);
    if (!res.success || !res.data) {
      toast.error(res.error?.message ?? 'Could not accept invite');
      return;
    }
    // Pin the new household as the active one + refresh the list, so
    // the dashboard renders under the new tenant scope.
    setActiveHouseholdId(res.data.household_id);
    setActiveId(res.data.household_id);
    await refresh();
    toast.success('You joined the household');
    navigate('/', { replace: true });
  };

  if (loading || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-700 border-t-emerald-400" />
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
        <div className="w-full max-w-md rounded-lg border border-rose-900 bg-rose-950/40 p-6 text-rose-200">
          <h2 className="mb-2 text-lg font-semibold">Invite unavailable</h2>
          <p className="text-sm opacity-90">{error ?? 'This invite is not valid.'}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 rounded-md border border-rose-700 bg-rose-900/50 px-3 py-1.5 text-sm hover:bg-rose-900"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (preview.status !== 'pending') {
    const labels: Record<string, string> = {
      accepted: 'This invite has already been used.',
      revoked: 'This invite was revoked by the household owner.',
      expired: 'This invite has expired. Ask the owner to send a new one.',
    };
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
        <div className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-900 p-6 text-zinc-200">
          <h2 className="mb-2 text-lg font-semibold">Invite {preview.status}</h2>
          <p className="text-sm opacity-80">{labels[preview.status]}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    );
  }

  const callerEmail = session?.user.email ?? '';
  const emailMismatch = callerEmail && callerEmail.toLowerCase() !== preview.invited_email.toLowerCase();

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
      <div className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-900 p-6">
        <h2 className="mb-1 text-xl font-semibold text-zinc-100">You're invited</h2>
        <p className="mb-4 text-sm text-zinc-400">
          Join <span className="font-semibold text-zinc-100">{preview.household_name}</span> as a
          {' '}
          <span className="font-semibold text-zinc-100">{preview.role}</span>.
        </p>

        {emailMismatch && (
          <div className="mb-4 rounded-md border border-amber-900 bg-amber-950/40 p-3 text-xs text-amber-200">
            <div className="font-medium">Email mismatch</div>
            <div className="mt-1 opacity-90">
              Sent to <code>{preview.invited_email}</code>; you're signed in as <code>{callerEmail}</code>.
              You can still accept — the link itself is the proof.
            </div>
          </div>
        )}

        <button
          onClick={onAccept}
          disabled={accepting}
          className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          {accepting ? 'Joining…' : `Join ${preview.household_name}`}
        </button>

        <button
          onClick={() => navigate('/')}
          className="mt-2 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
